// #region ##################################################################################### IMPORTS
// ---------------------------------------------------------------------- NORMAL IMPORTS
import {
  createBrowserRouter,
  redirect,
  RouterProvider,
} from "react-router-dom";
import LoginScreen from "@screens/LoginScreen";
import NotFoundScreen from "@screens/NotFoundScreen";
import HomeScreen from "@screens/HomeScreen";
import * as _T from "@utils/ClassTypes";
import { parseID, useRefresh, userSession } from "scripts/FunctionsBundle";
import { initializeApp } from "firebase/app";
import * as FS from "firebase/firestore";
import * as AO from "firebase/auth";
import AdminScreen from "@screens/AdminScreen";
import SacramentosScreen from "@screens/SacramentosScreen";
import SacramentosAction from "@screens/SacramentosAction";
import MenuComponent from "@components/MenuComponent";
import AdminAction from "@screens/AdminAction";
import SeccionesScreen from "@screens/SeccionesScreen";

// ---------------------------------------------------------------------- TYPESCRIPT IMPORTS
type _LoaderFunctionArgs = import("react-router-dom").LoaderFunctionArgs;
type _ActionFunctionArgs = import("react-router-dom").ActionFunctionArgs;
// #endregion

// #region ##################################################################################### FIREBASE INIT
export const firebaseApp = initializeApp({
  apiKey: process.env.REACT_APP_FIREBASE_APIKEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTHDOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECTID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGEBUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGINGSENDERID,
  appId: process.env.REACT_APP_FIREBASE_APPID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENTID,
});
const db = FS.getFirestore(firebaseApp); // FIRESTORE (data db)
const auth = AO.getAuth(firebaseApp); // AUTHETICATION (users db)
// AO.onAuthStateChanged(auth, getCurrentUserInfo);
auth.setPersistence(AO.browserLocalPersistence);
// #endregion

// #region ##################################################################################### FIRESTORE
// ---------------------------------------------------------------------- FS PATH PROPS
/**
 * Props para usarse con {@link FSPath}.
 */
export type FSPathProps =
  | {
      subcoll: "usuarios";
      data: _T.OnlyKeys<_T.Usuario> & { id: string };
    }
  | {
      subcoll: "bautizos";
      data: _T.OnlyKeys<_T.Bautizo> & { id: string };
    }
  | {
      subcoll: "comuniones";
      data: _T.OnlyKeys<_T.Comunion> & { id: string };
    }
  | {
      subcoll: "confirmaciones";
      data: _T.OnlyKeys<_T.Confirmacion> & { id: string };
    }
  | {
      subcoll: "matrimonios";
      data: _T.OnlyKeys<_T.Matrimonio> & { id: string };
    }
  | {
      subcoll: "secciones";
      data: _T.OnlyKeys<_T.Seccion> & { id: string };
    }
  | {
      subcoll: "nichos";
      data: _T.OnlyKeys<_T.Nicho> & { id: string };
    }
  | {
      subcoll: "beneficiarios";
      data: _T.OnlyKeys<_T.Beneficiario> & { id: string };
    }
  | {
      subcoll: "cenizas";
      data: _T.OnlyKeys<_T.Ceniza> & { id: string };
    };

// const a: FSPathProps = { subcoll: "bautizos", data: {} };

// ---------------------------------------------------------------------- FS PATH
/**
 * Función auxiliar para crear un `path` válido con el cual acceder a `Firestore`.
 *
 * **Si queremos acceder a la información normal del `currentUser` no se aplican parámetros `req = undefined`.**
 * @param req Parámetros para poder moverse entre las sub-colecciones. Si no establecemos `userID` se toma el `currentUser.id`.
 * @returns Un `string` separado por slash-es que conduce a una colección de `Firestore`.
 */
// function FSPath(req: FSPathProps) {}

// ---------------------------------------------------------------------- FS ACTION
/**
 * Función con diversas utilidades para leer y manipular información dentro de `Firestore` (FS) rápidamente.
 * @param action Tipo de acción a realizar:
 *  1. `"read"`: Lee información de un documento en concreto a través de su ID.
 *  2. `"getall"`: Similar a `"read"`, pero con todos los documentos de la colección indicada.
 *  3. `"add"`: Guarda un nuevo registro dentro de la colección indicada con un ID aleatorio.
 *  4. `"insert"`: Reemplaza toda la info de un registro en concreto a través de su ID (si no existe, se crea).
 *  5. `"update"`: Modifica solamente los datos indicados de un registro en concreto a través de su ID (si no existe, marca error).
 *  6. `"delete"`: Elimina un registro a través de su ID.
 * @param options Información adicional para las subcolecciones del usuario, si no establecemos nada se busca en `users`.
 *  1. `subcoll`: Para saber si estamos entrando a `people` o `studies` del usuario.
 *  2. `personID`: Para buscar estudios de una persona del usuario. Independientemente de la `subcoll` establecida.
 *  3. `userID`: Para saber desde qué usuario estamos leyendo la subcolección, default: `currentUser.id`.
 * @param data Información con la cual trabajar. Debe contener al menos `{ id: "" }`.
 * @returns `null` siempre que la operación falle. `object` en caso de leer, o el mismo param `data` de otra manera.
 */
export function FSAction(
  action: "read" | "getall" | "add" | "insert" | "update" | "delete",
  subcoll: string,
  data: { [x: string]: any } & { id: string | null }
) {
  return new Promise<object | null>(async (resolve) => {
    // -------------------------------------------------- INSUFFICIENT DATA
    if (!GS.parroquia.id || data.id === null) {
      GS.setAlert({
        _type: "error",
        _message: !auth.currentUser
          ? "Es necesario iniciar sesión para realizar esta acción."
          : "Hace falta ingresar un ID de búsqueda válido.",
      });
      return resolve(null);
    }

    subcoll = parseID(subcoll);
    const docID = data.id;
    data = parseToFS(data);
    const colref = FS.collection(db, "parroquias", GS.parroquia.id, subcoll);

    if (action === "read") {
      const docref = FS.doc(colref, docID);
      const snap = await FS.getDoc(docref).catch((e) => {
        console.error(e);
        return null;
      });
      if (!snap || !snap.exists()) resolve(null);
      else resolve({ ...parseToFS(snap.data()), id: snap.id });
    }

    if (action === "getall") {
      const snap = await FS.getDocs(colref).catch((e) => {
        console.error(e);
        return null;
      });
      const resp: any[] = [];
      if (!snap || snap.empty) resolve(null);
      else {
        snap.forEach((doc) =>
          resp.push({ ...parseToFS(doc.data()), id: doc.id })
        );
        resolve(resp);
      }
    }

    if (action === "add") {
      const docref = await FS.addDoc(colref, data).catch((e) => {
        console.error(e);
        return null;
      });
      if (!docref) resolve(null);
      else {
        data.id = docref.id;
        resolve(data);
      }
    }

    if (action === "insert") {
      const docref = FS.doc(colref, docID);
      await FS.setDoc(docref, data).catch((e) => {
        console.error(e);
        resolve(null);
      });
      resolve(data);
    }

    if (action === "update") {
      const docref = FS.doc(colref, docID);
      await FS.updateDoc(docref, data).catch((e) => {
        console.error(e);
        resolve(null);
      });
      resolve(data);
    }

    if (action === "delete") {
      const docref = FS.doc(colref, docID);
      await FS.deleteDoc(docref).catch((e) => {
        console.error(e);
        resolve(null);
      });
      resolve(data);
    }

    resolve(null);
  });
}

// ------------------------------------------ PARSE TO FS
/**
 * Remueve las propiedades de `_specifics`, `update` y `id`, y convierte todos los `Date` en `Timestamp` de Firebase de momento.
 * Otras propiedades que sean funciones o `undefined` simplemente las omite.
 * @param obj Objeto a transformar.
 * @returns Un nuevo objeto para ingresar directamente a FS (puede contener mutaciones).
 */
export function parseToFS(obj: any) {
  if (!obj || typeof obj !== "object" || obj instanceof Date) return null;
  let newobj: any;

  if (Array.isArray(obj)) {
    newobj = [];

    for (let item of obj) {
      if (
        typeof item === "boolean" ||
        typeof item === "number" ||
        typeof item === "string"
      )
        newobj.push(item);
      else if (typeof item === "function" || item === undefined) continue;
      else if (item instanceof Date) newobj.push(FS.Timestamp.fromDate(item));
      else if (item instanceof FS.Timestamp) newobj.push(item.toDate());
      else newobj.push(parseToFS(item));
    }
  } else {
    const { update, _specifics, id, ...rest } = obj;
    newobj = {};

    for (const key in rest) {
      const item = rest[key];
      if (
        typeof item === "boolean" ||
        typeof item === "number" ||
        typeof item === "string"
      )
        newobj[key] = item;
      else if (typeof item === "function" || item === undefined) continue;
      else if (item instanceof Date) newobj[key] = FS.Timestamp.fromDate(item);
      else if (item instanceof FS.Timestamp) newobj[key] = item.toDate();
      else newobj[key] = parseToFS(item);
    }
  }

  return newobj;
}

// ------------------------------------------ UPDATE PARROQUIA
/**
 * Toma la información guardada dentro de `GS.parroquia` y la actualiza en FS.
 * Si no hay parroquia o el usuario no es administrador, marca error.
 */
export async function FSParroquia() {
  if (!GS.parroquia.id || GS.currentUser?.id !== "admin") {
    GS.setAlert({
      _type: "error",
      _message: "No cuenta con los permisos necesarios.",
    });
    return false;
  }

  const docref = FS.doc(db, "parroquias", GS.parroquia.id);
  return await FS.updateDoc(docref, parseToFS(GS.parroquia))
    .then(() => {
      GS.setAlert({
        _type: "success",
        _message: "Se ha guardado la información.",
      });
      return true;
    })
    .catch((e) => {
      GS.setAlert({
        _type: "error",
        _message: "Algo salió mal... revise su conexión e inténtelo de nuevo.",
      });
      console.error(e);
      return false;
    });
}
// #endregion

// #################################################################################### AUTHENTICATION

// ------------------------------------------ LOG IN USER
/**
 * Inicia una sesión de usuario para trabajar en la parroquia indicada bajo los permisos otorgados.
 * @param email Email de la parroquia en donde iniciar sesión.
 * @param user Nombre de usuario con el cual iniciar sesión.
 * @param pass Contraseña para inicar sesión en el usuario.
 * @param searchSession Indica si se buscará o no una sessión registrada de usuario. Default: `false`.
 */
export async function LogIn(
  cred?: {
    email: string;
    user: string;
    pass: string;
  },
  searchSession: boolean = false
) {
  // ------------------------------------------ INITIAL VALUES
  // En caso de que queramos buscar alguna sesión guardada.
  const session = searchSession ? userSession() : undefined;
  if (!cred && !session) return; // No hay sesiones

  cred = {
    email: session?.email || parseID(cred!.email),
    user: session?.user || cred!.user,
    pass: session?.pass || cred!.pass,
  };

  if (
    !cred.email ||
    !cred.pass ||
    !cred.user ||
    (GS.parroquia.email === cred.email && GS.currentUser?.user === cred.user)
  )
    return;

  // ------------------------------------------ QUERY & SNAP PARROQUIAS
  let query = FS.query(
    FS.collection(db, "parroquias"),
    FS.where("email", "==", cred.email)
  );

  if (cred.user === "admin") {
    query = FS.query(
      FS.collection(db, "parroquias"),
      FS.where("email", "==", cred.email),
      FS.where("pass", "==", cred.pass)
    );
  }

  let snap = await FS.getDocs(query).catch((e) => {
    console.error(e);
    return null;
  });

  // ======== ERROR
  if (!snap || snap.empty) {
    // O no hay sesión guardada, o la que está no funcionó. Borramos todo
    userSession(undefined, true);

    GS.setAlert({
      _type: "error",
      _message: "No se encontró ninguna parroquia con el email ingresado.",
    });
    return;
  }

  // ------------------------------------------ SAVE PARROQUIA
  const parr = new _T.Parroquia({
    ...snap.docs[0].data(),
    id: snap.docs[0].id,
  });

  // ------------------------------------------ SEARCH ADMIN SESSION
  if (cred.user === "admin") {
    GS.parroquia = parr;
    GS.currentUser = new _T.Usuario({
      permiso_nichos: 1,
      permiso_sacramentos: 1,
      user: cred.user,
      id: "admin",
    });
    userSession(cred);
    // THIS ONLY ENABLES WRITE MODE ON DB
    AO.signInWithEmailAndPassword(
      auth,
      process.env.REACT_APP_COMMON_USER || "ERROR",
      process.env.REACT_APP_COMMON_USER_PASS || "ERROR"
    );
    GS.setAlert({
      _type: "success",
      _message: "Ha iniciado sesión como administrador",
    });
    GS.refresh();
    return;
  }

  // ------------------------------------------ QUERY & SNAP USUARIOS
  query = FS.query(
    FS.collection(db, "parroquias", parr.id || "test", "usuarios"),
    FS.where("user", "==", cred.user),
    FS.where("pass", "==", cred.pass)
  );

  snap = await FS.getDocs(query).catch((e) => {
    console.error(e);
    GS.setAlert({
      _type: "error",
      _message: "Algo salió mal... revise su conexión e inténtelo de nuevo.",
    });
    return null;
  });

  // ======== ERROR
  if (!snap || snap.empty) {
    GS.setAlert({
      _type: "error",
      _message: "Usuario y/o contraseña incorrectas.",
    });
    return;
  }

  // Se encontró al usuario, lo almacenamos de momento.
  const us = new _T.Usuario({
    ...snap.docs[0].data(),
    id: snap.docs[0].id,
  });

  GS.parroquia = parr;
  GS.currentUser = us;
  userSession(cred);
  // THIS ONLY ENABLES WRITE MODE ON DB
  AO.signInWithEmailAndPassword(
    auth,
    process.env.REACT_APP_COMMON_USER || "ERROR",
    process.env.REACT_APP_COMMON_USER_PASS || "ERROR"
  );
  GS.setAlert({
    _type: "success",
    _message: `Ha iniciado sesión el usuario: ${us.user}!`,
  });
  GS.refresh();
}

// ------------------------------------------ LOG OUT USER
/**
 * Eliminar toda información almacenada en `GS` {@link GS}.
 */
export async function LogOut() {
  await auth.signOut();
  // clear all from IDB // debug
  GS.parroquia = new _T.Parroquia();
  GS.currentUser = null;
  GS.servicios = {};
  userSession(undefined, true);
  GS.refresh();
}

// #################################################################################### LOADERS & ACTIONS

// ------------------------------------------ USER CHECK --------------------- LVL 0
/**
 * Revisa si hay las cosas básicas.
 */
async function userCheck(req: _LoaderFunctionArgs) {
  // ########################################## ERROR CHECK
  if (!GS.currentUser?.id || !GS.parroquia.id) {
    // --------------------- NO HAY USUARIO || NO HAY PARROQUIA
    throw new Response("No autorizado", {
      status: 401,
      statusText:
        "Se necesita una sesión válida para acceder a este contenido.",
    });
  }

  return null;
}

// ################################ CHECKS

// ------------------------------------------ SACRAMENTOS CHECK --------------------- LVL 1
/**
 * Revisa a ver si el usuario tiene permisos para ver este contenido, o si existe el contenido.
 */
async function sacramentosCheck(req: _LoaderFunctionArgs) {
  await userCheck(req); // --------------------- RE-CHECK

  // ########################################## ERROR CHECK
  if (!GS.currentUser || GS.currentUser.permiso_sacramentos < 0) {
    // --------------------- EL USUARIO NO TIENE PERMISOS
    throw new Response("No autorizado", {
      status: 401,
      statusText: "No cuenta con permisos suficientes para ver este contenido.",
    });
  } else if (!GS.parroquia.sacramentos) {
    // --------------------- LA PARROQUIA NO TIENE SACRAMENTOS
    throw new Response("No existe", {
      status: 404,
      statusText: "Esta parroquia no cuenta con servicio de sacramentos.",
    });
  }

  return null;
}

// ------------------------------------------ NICHOS CHECK --------------------- LVL 1
/**
 * Revisa a ver si el usuario tiene permisos para ver este contenido, o si existe el contenido.
 */
async function nichosCheck(req: _LoaderFunctionArgs) {
  await userCheck(req); // --------------------- RE-CHECK

  // ########################################## ERROR CHECK
  if (!GS.currentUser || GS.currentUser.permiso_nichos < 0) {
    // --------------------- EL USUARIO NO TIENE PERMISOS
    throw new Response("No autorizado", {
      status: 401,
      statusText: "No cuenta con permisos suficientes para ver este contenido.",
    });
  } else if (!GS.parroquia.nichos) {
    // --------------------- LA PARROQUIA NO TIENE NICHOS
    throw new Response("No existe", {
      status: 404,
      statusText: "Esta parroquia no cuenta con servicio de nichos.",
    });
  }

  return null;
}

// ################################ LOADERS

// ------------------------------------------ ADMIN LOADER --------------------- LVL 2
/**
 * Revisa a ver si el usuario tiene permisos para ver este contenido, o si existe el contenido.
 */
async function adminLoader(req: _LoaderFunctionArgs, bubble: unknown = false) {
  const { id } = req.params;

  await userCheck(req); // --------------------- RE-CHECK

  // ########################################## ERROR CHECK
  if (GS.currentUser?.id !== "admin") {
    // --------------------- EL USUARIO NO TIENE PERMISOS
    throw new Response("No autorizado", {
      status: 401,
      statusText: "No cuenta con permisos suficientes para ver este contenido.",
    });
  }

  // --------------------- AQUÍ SE ACABAN LAS VERIFICACIONES Y COMIENZA EL loader
  if (bubble) return null;

  // --------------------- REINICIAR LOS SERVICIOS Y LEER DATOS
  GS.servicios = {};
  const res = await FSAction("getall", "usuarios", { id: "id" }).catch((e) => {
    console.error(e);
    return null;
  });
  GS.servicios.usuarios = (res as any) || [];
  const res_all = GS.servicios.usuarios || [];
  let res_one: _T.Usuario | undefined = undefined;

  // --------------------- BUSCAR UN ELEMENTO POR ID SI ES QUE HAY
  if (id) {
    res_one = res_all.find((el) => el.id === id);

    if (!res_one) {
      throw new Response("", {
        status: 404,
        statusText: `No se encontró  al usuario "${id}".`,
      });
    }
  }

  // --------------------- REGRESAR LO QUE ENCONTRAMOS
  return {
    res_all: structuredClone(res_all),
    res_one: structuredClone(res_one),
  };
}

// ------------------------------------------ SACRAMENTOS LOADER --------------------- LVL 2
/**
 * Busca información de los sacramentos según lo que se ingresó.
 * @returns Un `res_all` con un arreglo de todos los registros, y un `res_one?` con un registro que se buscó por su ID (si existe).
 */
async function sacramentosLoader(
  req: _LoaderFunctionArgs,
  bubble: unknown = false
) {
  // --------------------- TIPOS Y VARIABLES TEMPORALES
  type a = _T.Bautizo | _T.Comunion | _T.Confirmacion | _T.Matrimonio;
  let res_all: a[] = [];
  let res_one: a | undefined;
  const { type, id } = req.params;
  const opt = ["bautizos", "comuniones", "confirmaciones", "matrimonios"];

  await sacramentosCheck(req); // --------------------- RE-CHECK

  // ########################################## ERROR CHECK
  if (!type || !opt.includes(type)) {
    // --------------------- NO SE ENCONTRÓ EL TIPO DE SACRAMENTO
    throw new Response("No encontrado", {
      status: 404,
      statusText: `No existe el sacramento "${type}".`,
    });
  }

  // --------------------- AQUÍ SE ACABAN LAS VERIFICACIONES Y COMIENZA EL loader
  if (bubble) return null;

  // --------------------- REINICIAR LOS SERVICIOS Y LEER DATOS
  GS.servicios = {};
  const res = await FSAction("getall", type, { id: "id" }).catch((e) => {
    console.error(e);
    return null;
  });
  GS.servicios[type] = res || [];
  res_all = GS.servicios[type];

  // --------------------- BUSCAR UN ELEMENTO POR ID SI ES QUE HAY
  if (id) {
    res_one = res_all.find((el) => el.id === id);

    if (!res_one) {
      throw new Response("", {
        status: 404,
        statusText: `No se encontró el registro "${id}" dentro de "${type}".`,
      });
    }
  }

  // --------------------- REGRESAR LO QUE ENCONTRAMOS
  return {
    res_all: structuredClone(res_all),
    res_one: structuredClone(res_one),
  };
}

// ################################ PRE-ACTION CHECK

// ------------------------------------------ SACRAMENTOS ACTION CHECK --------------------- LVL 3
/**
 * Primero revisa si hay una acción válida como new, edit o delete. Después revisa si hay algo guardado en el
 * cache del GS para no tener que buscar de nuevo. Si no hay nada guardado o el ID del elemento no es igual
 * al que está en los parámetros, entonces marca error, porque debería siempre existir (si no existe en el GS
 * significa que se accedió directamente con la URL y esto es peligroso, entonces mejor redireccionamos a la
 * página de SacramentosScreen donde se re-verificará que el usuario tiene accesos correctos).
 */
async function sacramentosActionCheck(req: _LoaderFunctionArgs) {
  // --------------------- TIPOS Y VARIABLES TEMPORALES
  const { id, action } = req.params;
  const act = ["new", "edit", "delete", "print"];

  await sacramentosLoader(req, true); // --------------------- RE-CHECK

  // ########################################## ERROR CHECK
  if (GS.currentUser?.permiso_sacramentos !== 1 && action !== "print") {
    // --------------------- EL USUARIO NO TIENE PERMISOS
    throw new Response("No autorizado", {
      status: 401,
      statusText:
        "No cuenta con permisos suficientes para realizar esta acción.",
    });
  } else if (
    !GS.cache?.el ||
    !id ||
    GS.cache.el.id !== id ||
    !action ||
    !act.includes(action)
  ) {
    // --------------------- NO SE ENCONTRÓ LA ACCIÓN A REALIZAR y/o INFORMACIÓN INSUFICIENTE
    GS.setAlert({
      _message: `No se encontró el registro "${id}".`,
      _type: "warning",
    });

    throw new Response("Error inesperado", {
      status: 400,
      statusText:
        "Ocurrió un error al momento de efectuar la acción,;;" +
        "es probable que la información proporcionada esté incompleta o mal elaborada.;;" +
        "Regrese a la página principal e intente de nuevo.",
    });
  }

  // --------------------- REGRESAMOS EL ELEMENTO DEL CACHE
  return { el: GS.cache.el };
}

// ------------------------------------------ ADMIN ACTION CHECK --------------------- LVL 3
/**
 * Igual que sacramentosActionCheck, solo que con el Admin. Este es más sencillo, porque nada más
 * tenemos que revisar si es admin o no, revisamos el elemento del caché y lo regresamos.
 */
async function adminActionCheck(req: _LoaderFunctionArgs) {
  // --------------------- TIPOS Y VARIABLES TEMPORALES
  const { id, action } = req.params;
  const act = ["new", "edit", "delete"];
  const elid = GS.cache?.el?.id as string | undefined;

  await adminLoader(req, true); // --------------------- RE-CHECK

  // ########################################## ERROR CHECK
  if (!GS.cache || elid !== id || !action || !act.includes(action)) {
    // --------------------- NO SE ENCONTRÓ LA ACCIÓN A REALIZAR y/o INFORMACIÓN INSUFICIENTE
    GS.setAlert({
      _message: `No se encontró el usuario "${id}".`,
      _type: "warning",
    });

    return redirect("/admin", {
      status: 404,
      statusText: `No se encontró el usuario "${id}".`,
    });
  }

  // --------------------- REGRESAMOS EL ELEMENTO DEL CACHE
  return { el: GS.cache.el };
}

// ################################ ACTIONS

// ------------------------------------------ SACRAMENTOS CRUD --------------------- LVL ACTION
/**
 * Primero revisamos si tiene permisos y si toda la información para trabajar es correcta, después vamos a
 * simplemente agregar el nuevo registro si se trata de `new`, o buscar el elemento por su ID y modificarlo
 * completamente si es `edit` o eliminarlo si es `delete`. El print hace lo mismo que el edit.
 * Finalmente, redireccionamos hacia la página de buscar sacramentos.
 */
async function sacramentosCRUD(req: _ActionFunctionArgs) {
  await sacramentosActionCheck(req); // --------------------- RE-CHECK

  type a = _T.Bautizo | _T.Comunion | _T.Confirmacion | _T.Matrimonio;
  const { type, id, action } = req.params;
  const arr = GS.servicios[type || "asd"] as a[] | undefined;

  // ########################################## ERROR CHECK
  if (!GS.parroquia.sacramentos || !arr) {
    throw new Response("No existe", {
      status: 404,
      statusText: "Esta parroquia no tiene servicio de sacramentos.",
    });
  }

  const pend = structuredClone(GS.cache.el);
  GS.cache = {};

  // --------------------- NEW
  if (action === "new") {
    const res = await FSAction("add", type!, pend).catch((e) => {
      console.error(e);
      return null;
    });

    if (!res) {
      GS.setAlert({
        _type: "error",
        _message: "Ha sucedido un error al momento de realizar la operación.",
      });
      return redirect("/sacramentos/" + type);
    }

    arr.push(res as any);
    GS.setAlert({
      _type: "success",
      _message: `El registro "${id}" se ha ingresado correctamente dentro de "${type}".`,
    });
    return redirect("/sacramentos/" + type + "/" + (res as any).id);
  }

  // --------------------- BUSCAR EL ELEMENTO ORIGINAL
  const index = arr.findIndex((v) => v.id === id);
  if (index === -1) {
    throw new Response("No encontrado", {
      status: 404,
      statusText: "No se encontró el registro a modificar.",
    });
  }

  // --------------------- DELETE
  if (action === "delete") {
    const res = await FSAction("delete", type!, { id: id ?? null }).catch(
      (e) => {
        console.error(e);
        return null;
      }
    );

    if (!res) {
      GS.setAlert({
        _type: "error",
        _message: "Ha sucedido un error al momento de realizar la operación.",
      });
      return redirect("/sacramentos/" + type);
    }

    arr.splice(index, 1);
    GS.setAlert({
      _type: "success",
      _message: `El registro "${id}" se ha eliminado correctamente de "${type}".`,
    });
    return redirect("/sacramentos/" + type);
  }

  // --------------------- EDIT
  if (action === "edit") {
    const res = await FSAction("insert", type!, pend).catch((e) => {
      console.error(e);
      return null;
    });

    if (!res) {
      GS.setAlert({
        _type: "error",
        _message: "Ha sucedido un error al momento de realizar la operación.",
      });
      return redirect("/sacramentos/" + type);
    }

    arr[index] = structuredClone(pend);
  }

  GS.setAlert({
    _type: "success",
    _message: `El registro "${id}" ha sido modificado correctamente en "${type}".`,
  });

  // --------------------- DEFAULT REDIRECT
  return redirect("/sacramentos/" + type + "/" + id);
}

// ------------------------------------------ ADMIN CRUD --------------------- LVL ACTION
/**
 * Primero revisamos si tiene permisos y si toda la información para trabajar es correcta, después vamos a
 * simplemente agregar el nuevo registro si se trata de `new`, o buscar el elemento por su ID y modificarlo
 * completamente si es `edit` o eliminarlo si es `delete`. Finalmente, redireccionamos de regreso a AdminScreen.
 */
async function adminCRUD(req: _ActionFunctionArgs) {
  const { id, action } = req.params;
  const act = ["new", "edit", "delete"];
  const arr = (
    GS.servicios["usuarios"]
      ? GS.servicios["usuarios"]
      : (GS.servicios["usuarios"] = [])
  ) as _T.Usuario[];

  // --------------------- ERRORES
  if (
    !action ||
    !act.includes(action) ||
    !id ||
    !GS.cache?.el ||
    id !== GS.cache.el.id
  ) {
    throw new Response("Error inesperado", {
      status: 400,
      statusText:
        "Ocurrió un error al momento de efectuar la acción,;;" +
        "es probable que la información proporcionada esté incompleta o mal elaborada.;;" +
        "Regrese a la página principal e intente de nuevo.",
    });
  } else if (GS.currentUser?.id !== "admin" || !GS.parroquia.id) {
    throw new Response("No autorizado", {
      status: 403,
      statusText: "No tienes permiso para realizar esta acción.",
    });
  }

  const pend = structuredClone(GS.cache.el);
  GS.cache = {};

  // --------------------- NEW
  if (action === "new") {
    const res = await FSAction("add", "usuarios", pend).catch((e) => {
      console.error(e);
      return null;
    });

    if (!res) {
      GS.setAlert({
        _type: "error",
        _message: "Ha sucedido un error al momento de realizar la operación.",
      });
      return redirect("/admin");
    }

    arr.push(res as any);
    GS.setAlert({
      _type: "success",
      _message: `El usuario "${id}" ha sido creado exitosamente.`,
    });
    return redirect("/admin/" + (res as any).id);
  }

  // --------------------- BUSCAR EL ELEMENTO ORIGINAL
  const index = arr.findIndex((v) => v.id === id);
  if (index === -1) {
    throw new Response("No encontrado", {
      status: 404,
      statusText: "No se encontró el usuario a modificar.",
    });
  }

  // --------------------- DELETE
  if (action === "delete") {
    const res = await FSAction("delete", "usuarios", { id }).catch((e) => {
      console.error(e);
      return null;
    });

    if (!res) {
      GS.setAlert({
        _type: "error",
        _message: "Ha sucedido un error al momento de realizar la operación.",
      });
      return redirect("/admin");
    }

    arr.splice(index, 1);
    GS.setAlert({
      _type: "success",
      _message: `El usuario "${id}" se ha eliminado exitosamente.`,
    });
    return redirect("/admin");
  }

  // --------------------- EDIT
  if (action === "edit") {
    const res = await FSAction("insert", "usuarios", pend).catch((e) => {
      console.error(e);
      return null;
    });

    if (!res) {
      GS.setAlert({
        _type: "error",
        _message: "Ha sucedido un error al momento de realizar la operación.",
      });
      return redirect("/admin");
    }

    arr[index] = structuredClone(pend);
  }

  GS.setAlert({
    _type: "success",
    _message: `El usuario "${id}" ha sido modificado correctamente.`,
  });

  // --------------------- DEFAULT REDIRECT
  return redirect("/admin/" + id);
}

// #################################################################################### GLOBAL STATE
/**
 * Objeto principal de estado global. Almacena toda la información de la base de datos relevante a la parroquia actual.
 */
export const GS = new _T.Global();

// #################################################################################### MAIN APPLICATION
function App() {
  const [refresh] = useRefresh();
  GS.refresh = refresh;

  return (
    <RouterProvider
      router={createBrowserRouter([
        {
          element: <HomeScreen />,
          loader: undefined,
          action: undefined,
          // ##################### MAIN ERROR [possibly unreachable] */
          errorElement: <NotFoundScreen />,
          children: [
            {
              // ##################### CHILDREN ERROR */
              errorElement: <NotFoundScreen />,
              children: [
                // ########################################## MAIN PATH */
                {
                  index: true,
                  element: <MenuComponent />,
                },
                // ########################################## SACRAMENTOS PATH */
                {
                  path: "sacramentos",
                  loader: sacramentosCheck,
                  action: undefined,
                  children: [
                    // ##################### INDEX PATH */
                    {
                      index: true,
                      element: <MenuComponent _nichos={false} />,
                    },
                    // ##################### TIPO + ID? PATH */
                    {
                      path: ":type/:id?",
                      element: <SacramentosScreen />,
                      loader: sacramentosLoader,
                      shouldRevalidate: ({
                        currentParams: { type: ct },
                        nextParams: { type: nt },
                      }) => ct !== nt,
                    },
                    // ##################### TIPO + ID - EDITAR */
                    {
                      path: ":type/:id/:action",
                      element: <SacramentosAction />,
                      loader: sacramentosActionCheck,
                      action: sacramentosCRUD,
                    },
                  ],
                },
                // ########################################## NICHOS PATH */
                {
                  path: "nichos",
                  loader: nichosCheck,
                  action: undefined,
                  children: [
                    // ##################### INDEX PATH */
                    {
                      index: true,
                      element: <MenuComponent _sacramentos={false} />,
                    },
                    // ##################### TIPO + ID? PATH */
                    {
                      path: ":type/:id?",
                      element: <SeccionesScreen />,
                      loader: async (req) => null,
                    },
                    // ##################### TIPO + ID PATH */
                    {
                      path: ":type/:id/print",
                      element: <>Pantalla de imprimir</>,
                      loader: async (req) => null,
                    },
                  ],
                },
                // ########################################## ADMIN PATH */
                {
                  path: "admin",
                  children: [
                    // ##################### TIPO + ID? PATH */
                    {
                      path: ":id?",
                      element: <AdminScreen />,
                      loader: adminLoader,
                      shouldRevalidate: ({
                        currentParams: cp,
                        nextParams: np,
                      }) => {
                        return !!(cp.action || np.action);
                      },
                    },
                    // ##################### TIPO + ID - EDITAR */
                    {
                      path: ":id/:action",
                      element: <AdminAction />,
                      loader: adminActionCheck,
                      action: adminCRUD,
                    },
                  ],
                },
                // ########################################## LOGIN PATH */
                {
                  path: "login",
                  element: <LoginScreen />,
                  loader: undefined,
                  action: undefined,
                },
              ],
            },
          ],
        },
        // ########################################## NOT FOUND SCREEN */
        {
          path: "*",
          element: <NotFoundScreen />,
        },
      ])}
    />
  );
}

export default App;
